import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { capitalize } from 'lodash'
import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import { css } from '@emotion/core'

import { AccountContext } from '@/contexts/account'
import type { AccountContextValue } from '@/contexts/account'
import { Button, ButtonType } from '@/components/atoms/button'
import { Select, Option } from '@/components/atoms/select'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { InputField } from '@/components/molecules/input-field'
import { NoticeLoading } from '@/components/molecules/notice-loading'
import { getEmployeeStaff, loginEmployeeStaff } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import type { StaffSessionTimeoutReason } from '@/libs/staff-mutation-guard-store'
import {
  applyEmployeeStaffSnapshot,
  clearStaffMutationSuccessTimestamp,
  getResolvedRequestUrl,
  invalidateLocalStaffAfterServerSessionTimeout,
  isExcludedFromStaffMutationGuard,
  isHotelApiUrl,
  recordStaffMutationSuccessTimestamp,
  registerStaffGuardModalOpener,
  rejectAllStaffGuardWaiters,
  requestStaffGuardModal,
  resolveAllStaffGuardWaiters,
  setPendingStaffSessionTimeoutReason,
  setStaffGuardModalBlocksGlobalLoader,
  StaffGuardSessionReauthAbortError,
  shouldHandleStaffSessionTimeoutResponse,
  shouldRecordStaffMutationSuccess,
  shouldSkipStaffMutationGuard,
  syncStaffGuardAccountState,
  takePendingStaffSessionTimeoutReason,
  unregisterStaffGuardModalOpener,
} from '@/libs/staff-mutation-guard-store'

/** 保存操作の途中で開くため、呼び出し元の画面モーダル（室料金設定 104、各種モーダル〜1300）より常に前面に出す */
const STAFF_GUARD_MODAL_Z_INDEX = 1500

const staffGuardModalContainerStyle = css({ zIndex: STAFF_GUARD_MODAL_Z_INDEX })

export const StaffMutationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const accountContext: AccountContextValue = useContext(AccountContext)
  const { account, employeeStaff, fetchEmployeeStaff, clearCurrentStaffSelection, replaceEmployeeStaffList } = accountContext

  const [modalOpen, setModalOpen] = useState(false)
  const [modalHeaderReason, setModalHeaderReason] = useState<StaffSessionTimeoutReason | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const clearSelectionRef = useRef<(() => void) | undefined>(undefined)
  const fetchStaffRef = useRef<typeof fetchEmployeeStaff | undefined>(undefined)
  /** 同一モーダル表示サイクルで openModal が複数回呼ばれても reason を一度だけ取り込む */
  const staffGuardModalCycleRef = useRef(false)

  useEffect(() => {
    clearSelectionRef.current = clearCurrentStaffSelection
  }, [clearCurrentStaffSelection])

  useEffect(() => {
    fetchStaffRef.current = fetchEmployeeStaff
  }, [fetchEmployeeStaff])

  const {
    control,
    handleSubmit,
    reset: resetForm,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<{ staffId: string; passCode: string }>({
    defaultValues: { staffId: '', passCode: '' },
  })

  const watchedStaffId = useWatch({ control, name: 'staffId' })

  useEffect(() => {
    syncStaffGuardAccountState({ employeeStaff: employeeStaff ?? [], account })
  }, [employeeStaff, account])

  const openModal = useCallback(() => {
    /** 同一表示サイクル内の重複 openModal では、選択解除・フォーム reset を繰り返さない */
    if (!staffGuardModalCycleRef.current) {
      staffGuardModalCycleRef.current = true
      const reason = takePendingStaffSessionTimeoutReason()
      setModalHeaderReason(reason != null && String(reason).length > 0 ? reason : null)
      clearSelectionRef.current?.()
      resetForm({ staffId: '', passCode: '' })
    }
    setStaffGuardModalBlocksGlobalLoader(true)
    setModalOpen(true)
  }, [resetForm])

  /** 一時的に reason 差分は出さず、表示文言は統一する。 */
  const staffModalTitle = useMemo(() => t('Please select a staff name.'), [t])

  useEffect(() => {
    registerStaffGuardModalOpener(openModal)
    return () => unregisterStaffGuardModalOpener()
  }, [openModal])

  useEffect(() => {
    const reqId = axios.interceptors.request.use(async config => {
      const method = config.method?.toLowerCase()
      if (!method) return config
      const fullUrl = getResolvedRequestUrl(config)
      const isWrite = ['post', 'put', 'patch', 'delete'].includes(method)
      const configWithStaffGuard = config as AxiosRequestConfig & { staffMutationTreatAsWrite?: boolean }
      const isExportLikeGet = method === 'get' && configWithStaffGuard.staffMutationTreatAsWrite === true
      if (!isWrite && !isExportLikeGet) return config
      if (!fullUrl || !isHotelApiUrl(fullUrl) || isExcludedFromStaffMutationGuard(fullUrl)) {
        return config
      }
      if (shouldSkipStaffMutationGuard()) return config
      try {
        await requestStaffGuardModal()
      } catch (e) {
        return Promise.reject(e)
      }
      return config
    })

    const resId = axios.interceptors.response.use(
      response => {
        const fullUrl = getResolvedRequestUrl(response.config)
        if (
          shouldRecordStaffMutationSuccess(fullUrl, response.config.method, response.config as { staffMutationTreatAsWrite?: boolean }) &&
          !shouldSkipStaffMutationGuard()
        ) {
          recordStaffMutationSuccessTimestamp()
        }
        return response
      },
      async error => {
        const status = error?.response?.status
        const code = error?.response?.data?.code
        const config = error?.config
        const fullUrl = getResolvedRequestUrl(config ?? {})
        /** BE が staff_guard を掛けた GET 等で、リクエスト側判定をすり抜けた場合も 403 で同じ UX に寄せる */
        const handleSessionTimeout = shouldHandleStaffSessionTimeoutResponse(fullUrl)

        if (status !== 403 || code !== 'STAFF_SESSION_TIMEOUT' || !config || !handleSessionTimeout) {
          return Promise.reject(error)
        }

        const reason = error?.response?.data?.reason as StaffSessionTimeoutReason | undefined

        invalidateLocalStaffAfterServerSessionTimeout()
        clearSelectionRef.current?.()
        // 一覧再取得はベストエフォート。失敗しても clearCurrentStaffSelection 済みのため
        // 「未選択」のまま維持され、古い isCurrent には戻さない（fetchEmployeeStaff は失敗時 setState しない）。
        try {
          await fetchStaffRef.current?.()
        } catch {
          /* errorHandler は fetchEmployeeStaff 内。ここでは握りつぶして未選択状態を維持 */
        }

        setPendingStaffSessionTimeoutReason(reason)
        try {
          await requestStaffGuardModal()
        } catch (e) {
          takePendingStaffSessionTimeoutReason()
          return Promise.reject(e)
        }
        // 再認証後も自動再送は行わず、元の操作は呼び出し元の再実行に委ねる。
        // 元の 403 を返すと errorHandler で二重通知になりやすいため、抑制用エラーに差し替える。
        return Promise.reject(new StaffGuardSessionReauthAbortError())
      },
    )

    return () => {
      axios.interceptors.request.eject(reqId)
      axios.interceptors.response.eject(resId)
    }
  }, [])

  const closeModal = () => {
    staffGuardModalCycleRef.current = false
    setStaffGuardModalBlocksGlobalLoader(false)
    setModalOpen(false)
    setModalHeaderReason(null)
    resetForm({ staffId: '', passCode: '' })
  }

  const onCancel = () => {
    rejectAllStaffGuardWaiters()
    closeModal()
  }

  const completeLoginAfterStaffSelect = async (staffId: string | number, passCode?: string) => {
    try {
      setSubmitting(true)
      await loginEmployeeStaff(staffId, passCode)
      clearStaffMutationSuccessTimestamp()
      const list = await getEmployeeStaff()
      const hasCurrent = Array.isArray(list) && list.some((s: any) => s?.isCurrent)
      const normalizedList =
        !hasCurrent && Array.isArray(list) ? list.map((s: any) => ({ ...s, isCurrent: String(s?.id) === String(staffId) })) : list
      applyEmployeeStaffSnapshot(normalizedList)
      replaceEmployeeStaffList?.(normalizedList)
      resolveAllStaffGuardWaiters()
      closeModal()
    } catch (e) {
      errorHandler(e)
    } finally {
      setSubmitting(false)
    }
  }

  const list = employeeStaff ?? []
  const selectedStaffForPin = list.find((s: any) => String(s?.id) === String(watchedStaffId))
  const staffSaveDisabled = submitting || !String(watchedStaffId ?? '').trim()

  const onSubmitStaffForm = async ({ staffId, passCode }: { staffId: string; passCode: string }) => {
    const staff = list.find((s: any) => String(s?.id) === String(staffId))
    if (!staff || submitting || staff.id == null) return
    await completeLoginAfterStaffSelect(staff.id, staff.hasPassCode ? passCode?.trim() || undefined : undefined)
  }

  const showModal = modalOpen && !shouldSkipStaffMutationGuard() && list.length > 0

  return (
    <>
      {children}
      {showModal && (
        <Modal
          customCss={{
            height: 'auto',
            width: 600,
            minWidth: 600,
            left: 'calc(50% - 300px)',
            justifyContent: 'flex-start',
          }}
          containerCss={staffGuardModalContainerStyle}
        >
          <ModalHeader>{staffModalTitle}</ModalHeader>
          <ModalBody customCss={{ height: 'auto', padding: '16px 32px 16px' }}>
            <NoticeLoading isLoading={submitting} />
            <div css={{ padding: 16, border: '1px solid #ddd', borderRadius: 8, backgroundColor: '#fff' }}>
              <p css={{ fontSize: 12, color: '#676767', marginBottom: 8 }}>{t('Full name')}</p>
              <Controller
                control={control}
                name="staffId"
                rules={{ required: t('Required field has not been entered') }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value || undefined}
                    onChange={v => {
                      onChange(v)
                      setValue('passCode', '')
                      clearErrors('passCode')
                    }}
                    placeholder={t('Please select')}
                    disabled={submitting}
                    error={errors.staffId?.message}
                    customStyle={css({ paddingBottom: 8 })}
                    // antd 既定（1050）だとモーダルの下に隠れる
                    dropdownStyle={{ zIndex: STAFF_GUARD_MODAL_Z_INDEX + 1 }}
                  >
                    {list.map((staff: any) => (
                      <Option key={staff.id} value={String(staff.id)}>
                        {staff.name}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              <Controller
                control={control}
                name="passCode"
                rules={{
                  validate: v => {
                    if (!selectedStaffForPin?.hasPassCode) return true
                    return v?.trim() ? true : t('Required field has not been entered')
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <InputField
                    value={value}
                    handleChangeData={onChange}
                    error={errors.passCode?.message}
                    label={capitalize(t('Passcode input'))}
                    marginBottom={0}
                    customInputStyle={css({ WebkitTextSecurity: 'disc' })}
                  />
                )}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button buttonType={ButtonType.Secondary} height="38px" width="110px" marginRight={16} onClick={onCancel}>
              {t('Cancel')}
            </Button>
            <Button
              buttonType={staffSaveDisabled ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              height="38px"
              width="110px"
              onClick={handleSubmit(onSubmitStaffForm)}
              isDisabled={staffSaveDisabled}
            >
              {t('Save')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}
